/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        LoginPage.tsx
 * @description Page de connexion avec support de la double authentification (2FA)
 *              Gere le flux en deux etapes lorsque la 2FA est activee
 *
 * @version     2.5
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { getErrorMessage } from '@/services/api';
import { ShieldCheckIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

/**
 * Page de connexion
 *
 * Gere deux etats :
 * 1. Formulaire email/password classique
 * 2. Formulaire de code 2FA (si l'utilisateur a active la 2FA)
 */
export function LoginPage() {
  const navigate = useNavigate();
  const { login, complete2FA, isLoading } = useAuthStore();

  // Etat du formulaire de connexion
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Etat pour la 2FA
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState('');

  /**
   * Gere la soumission du formulaire de connexion (etape 1)
   */
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const result = await login({ email, password });

      if (result.requires2FA && result.tempToken) {
        // 2FA requise - passer a l'etape 2
        setRequires2FA(true);
        setTempToken(result.tempToken);
        toast.success('Veuillez entrer votre code de verification');
      } else {
        // Connexion reussie sans 2FA
        handleLoginSuccess();
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  /**
   * Gere la soumission du code 2FA (etape 2)
   */
  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tempToken) {
      toast.error('Session expiree, veuillez recommencer');
      handleReset();
      return;
    }

    try {
      await complete2FA(tempToken, otpCode);
      handleLoginSuccess();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  /**
   * Gere la redirection apres connexion reussie
   */
  const handleLoginSuccess = () => {
    toast.success('Connexion reussie');

    // Recuperer l'utilisateur apres login pour verifier son role
    const user = useAuthStore.getState().user;

    // Rediriger les agents terrain vers l'interface mobile
    if (
      user?.roles.includes('ROLE_AGENT_DENEIGNEMENT') &&
      !user?.roles.includes('ROLE_CHEF_DENEIGNEMENT') &&
      !user?.roles.includes('ROLE_GID_REGUL') &&
      !user?.roles.includes('ROLE_ADMIN')
    ) {
      navigate('/agent');
    } else {
      navigate('/');
    }
  };

  /**
   * Reinitialise le formulaire pour revenir a l'etape 1
   */
  const handleReset = () => {
    setRequires2FA(false);
    setTempToken(null);
    setOtpCode('');
    setPassword('');
  };

  // Composant spinner de chargement
  const LoadingSpinner = () => (
    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo_falcon.png" alt="FALCON" className="h-20 mx-auto mb-4" />
          <p className="text-gray-600">Module Deneigement des Parkings</p>
        </div>

        {/* Formulaire */}
        <div className="card">
          <div className="card-body">
            {!requires2FA ? (
              // ========================================
              // ETAPE 1: Formulaire Email/Password
              // ========================================
              <form onSubmit={handleLoginSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="label">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    placeholder="votre@email.com"
                    required
                    autoFocus
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="label">
                    Mot de passe
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input"
                    placeholder="********"
                    required
                    disabled={isLoading}
                  />
                </div>

                <button type="submit" disabled={isLoading} className="btn-primary w-full">
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <LoadingSpinner />
                      Connexion...
                    </span>
                  ) : (
                    'Se connecter'
                  )}
                </button>
              </form>
            ) : (
              // ========================================
              // ETAPE 2: Formulaire Code 2FA
              // ========================================
              <form onSubmit={handle2FASubmit} className="space-y-4">
                {/* Indicateur 2FA */}
                <div className="text-center mb-4">
                  <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-3">
                    <ShieldCheckIcon className="w-8 h-8 text-primary-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Verification en deux etapes
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Entrez le code a 6 chiffres de votre application d'authentification
                  </p>
                </div>

                <div>
                  <label htmlFor="otpCode" className="label text-center block">
                    Code de verification
                  </label>
                  <input
                    id="otpCode"
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input text-center text-2xl tracking-widest font-mono"
                    placeholder="000000"
                    maxLength={6}
                    autoFocus
                    autoComplete="one-time-code"
                    disabled={isLoading}
                  />
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Ou utilisez un code de secours
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || otpCode.length < 6}
                  className="btn-primary w-full"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <LoadingSpinner />
                      Verification...
                    </span>
                  ) : (
                    'Verifier'
                  )}
                </button>

                {/* Bouton retour */}
                <button
                  type="button"
                  onClick={handleReset}
                  className="w-full flex items-center justify-center text-sm text-gray-500 hover:text-gray-700 mt-2"
                >
                  <ArrowLeftIcon className="w-4 h-4 mr-1" />
                  Utiliser un autre compte
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Groupe 3S Alyzia - FALCON v{__APP_VERSION__}
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
